import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Star, Calendar, ArrowRight, ArrowUpRight } from 'lucide-react';
import marasunset from '../assets/marasunset.jpg';
import elephant from '../assets/elephant.jpg';
import luxurysafari from '../assets/luzury.jpg';
import maraImg from '../assets/mara.jpg';
import serengetiImg from '../assets/serengeti.avif';
import landcruiser from '../assets/landcruiser.webp';
import { DESTINATIONS } from '../data/destinations';
import { testimonials } from '../data/testimonials';
import Reveal from './motion/Reveal';
import HeroReveal from './motion/HeroReveal';
import FeaturesBar from './FeaturesBar'
import FeaturedExcursions from './FeaturedExcursions';
import { getBlogPosts } from './blogData'; // Import your blog data function
import PageMeta from './PageMeta';

const HeroBanner = () => (
  <div
    className="relative min-h-[100svh] bg-cover bg-center flex items-center justify-center text-white"
    style={{
        backgroundImage: `url(${marasunset})`,
        backgroundBlendMode: 'overlay',
        backgroundColor: 'rgba(0,0,0,0.45)'
      }}
  >
    <div className="absolute inset-0 bg-gradient-to-b from-cafe-noir/60 via-cafe-noir/20 to-cafe-noir/70"></div>
    <HeroReveal as="div" className="text-center max-w-3xl px-4 relative z-10">
      <p className="uppercase tracking-[0.3em] text-sm text-hunyadi-yellow mb-5">Kenya · Tanzania · Rwanda · South Africa · Zanzibar</p>
      <h1 className="text-5xl md:text-7xl font-light mb-8 leading-[1.04] tracking-tight text-balance">The wild, the way it should be seen</h1>
      <p className="text-lg md:text-xl mb-9 max-w-xl mx-auto text-white/85 leading-relaxed">
        Small-group safaris across East and Southern Africa, planned around you by guides who were born to the bush.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Link
          to='/enquire'
          className="bg-hunyadi-yellow hover:bg-gold-metallic text-cafe-noir px-8 py-4 rounded-full text-lg font-medium transition-[background-color,transform] duration-300 hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-hunyadi-yellow/40 inline-block"
        >
          Plan Your Safari
        </Link>
        <Link
          to='/safaris'
          className="bg-transparent border border-white/60 hover:border-white hover:bg-white/10 px-8 py-4 rounded-full text-lg font-medium transition-[background-color,border-color,transform] duration-300 hover:-translate-y-0.5 inline-block"
        >
          Explore Destinations
        </Link>
      </div>
    </HeroReveal>
    <div className="absolute bottom-8 left-0 right-0 flex justify-center text-white/70">
      <ArrowRight className="transform rotate-90" size={28} aria-hidden="true" />
    </div>
  </div>
);



const FeatureSection = () => (
  <section className="py-24 lg:py-32">
    <div className="container mx-auto px-4">
      <div className="max-w-xl mx-auto text-center mb-16">
        <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-6 text-cafe-noir">Why choose Jambo Travel House</h2>
        <div className="h-px w-16 bg-clay mx-auto mb-6"></div>
        <p className="text-cafe-noir text-lg">We combine luxury and adventure to create unforgettable safari experiences that connect you with nature's most spectacular wonders.</p>
      </div>
     
     
       <FeaturesBar />
    </div>
   
  </section>
);

// Slim About teaser → links to the dedicated /about page.
const AboutTeaser = () => (
  <section className="py-24 lg:py-32 bg-white border-y border-cafe-noir/10">
    <div className="max-w-6xl mx-auto px-6 md:px-8">
      <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
        <div className="aspect-[4/3] overflow-hidden rounded-2xl bg-cornsilk">
          <img src={luxurysafari} alt="On safari with Jambo Travel House" loading="lazy" className="w-full h-full object-cover" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-brown-sugar font-medium mb-5">About Jambo</p>
          <h2 className="text-3xl md:text-4xl font-light text-cafe-noir tracking-tight leading-snug mb-6">Planned by people who live it</h2>
          <p className="text-cafe-noir/75 leading-relaxed mb-8">
            We’re a Kenya-based safari company with over 20 years turning travellers’ dreams into carefully planned
            journeys across East and Southern Africa — with the local knowledge and flexibility every trip deserves.
          </p>
          <Link to="/about" className="inline-flex items-center gap-2 rounded-full bg-hunyadi-yellow text-cafe-noir px-8 py-3.5 font-medium hover:bg-brown-sugar hover:text-white transition-colors">
            Our story <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

// Ways to travel — the 4 core services, giving Hotels + Car Hire homepage presence.
const SERVICES = [
  { title: 'Safaris', copy: 'Tailor-made game-viewing journeys across five countries.', image: maraImg, to: '/safaris', cta: 'Explore safaris' },
  { title: 'Excursions', copy: 'Day trips and coastal experiences in Mombasa & Nairobi.', image: serengetiImg, to: '/excursions', cta: 'Browse excursions' },
  { title: 'Stays', copy: 'Handpicked lodges, camps and hotels with contract rates.', image: luxurysafari, to: '/directory', cta: 'Find a stay' },
  { title: 'Car Hire', copy: '4x4s and safari vehicles with experienced drivers.', image: landcruiser, to: '/car-hire', cta: 'Hire a vehicle' },
];

const WaysToTravel = () => (
  <section className="py-24 lg:py-32 bg-eggshell">
    <div className="max-w-6xl mx-auto px-6 md:px-8">
      <div className="max-w-2xl mb-14">
        <p className="text-xs uppercase tracking-[0.22em] text-brown-sugar font-medium mb-4">Ways to travel with us</p>
        <h2 className="text-4xl md:text-5xl font-light tracking-tight text-cafe-noir">Everything for your African trip</h2>
      </div>
      <Reveal as="div" stagger={0.08} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {SERVICES.map((s) => (
          <Link key={s.title} to={s.to} className="group flex flex-col rounded-2xl overflow-hidden border border-cafe-noir/10 bg-white hover:border-brown-sugar/40 transition-colors">
            <div className="aspect-[4/3] overflow-hidden bg-cornsilk">
              <img src={s.image} alt={s.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
            </div>
            <div className="flex flex-col flex-grow p-6">
              <h3 className="text-xl font-light text-cafe-noir tracking-tight mb-2">{s.title}</h3>
              <p className="text-sm text-cafe-noir/65 leading-relaxed mb-5">{s.copy}</p>
              <span className="mt-auto inline-flex items-center gap-1.5 text-sm text-brown-sugar font-medium">{s.cta} <ArrowUpRight size={15} /></span>
            </div>
          </Link>
        ))}
      </Reveal>
    </div>
  </section>
);

// Where to Roam — clean country tiles, consistent with the /safaris hub.
const COUNTRY_ORDER = ['kenya', 'tanzania', 'rwanda', 'southafrica', 'zanzibar'];

const DestinationsSection = () => {
  const countries = COUNTRY_ORDER.map((k) => DESTINATIONS[k]).filter(Boolean);
  return (
    <section className="py-24 lg:py-32 bg-white border-y border-cafe-noir/10">
      <div className="max-w-6xl mx-auto px-6 md:px-8">
        <div className="max-w-2xl mx-auto text-center mb-16">
          <p className="text-xs uppercase tracking-[0.22em] text-brown-sugar font-medium mb-5">Where to roam</p>
          <h2 className="text-4xl md:text-5xl font-light tracking-tight text-cafe-noir leading-tight mb-6">
            Five countries, one safari you’ll never forget
          </h2>
          <p className="text-lg font-light text-cafe-noir/70 leading-relaxed">
            From the Mara’s migration to Rwanda’s gorilla forests and Zanzibar’s reefs — each journey is built around what you most want to see.
          </p>
        </div>
        <Reveal as="div" stagger={0.08} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {countries.map((d) => (
            <Link key={d.key} to={`/safaris/${d.key}`} className="group relative overflow-hidden rounded-2xl aspect-[4/5] block">
              <img src={d.heroImage} alt={`${d.name} safari`} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute inset-0 p-6 flex flex-col justify-end text-white">
                <p className="text-xs uppercase tracking-[0.2em] text-cornsilk/90 mb-2">{d.signature}</p>
                <h3 className="text-3xl font-light tracking-tight leading-none mb-3">{d.name}</h3>
                <span className="inline-flex items-center gap-1.5 text-sm text-white/90">
                  {d.packages?.length || 0} {d.packages?.length === 1 ? 'journey' : 'journeys'}
                  <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          ))}
        </Reveal>
        <div className="text-center mt-14">
          <Link to="/safaris" className="inline-flex items-center gap-2 rounded-full border border-cafe-noir text-cafe-noir px-8 py-3.5 font-medium hover:bg-cafe-noir hover:text-eggshell transition-colors">
            Explore all safaris <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
};

const TestimonialsSection = () => (
  <section className="max-w-6xl mx-auto px-6 md:px-8 py-20 md:py-28">
    <div className="flex flex-wrap items-end justify-between gap-4 mb-14">
      <div className="max-w-2xl">
        <p className="text-xs uppercase tracking-[0.22em] text-brown-sugar font-medium mb-4">In their words</p>
        <h2 className="text-4xl md:text-5xl font-light text-cafe-noir tracking-tight">What our travellers say</h2>
      </div>
      <Link to="/reviews" className="inline-flex items-center gap-1.5 text-sm text-brown-sugar font-medium hover:gap-2.5 transition-all">
        Read more reviews <ArrowRight size={15} />
      </Link>
    </div>
    <Reveal as="div" stagger={0.08} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {testimonials.slice(0, 3).map((t) => (
        <figure key={t.name} className="flex flex-col rounded-2xl border border-cafe-noir/10 bg-white p-6">
          <div className="flex gap-0.5 mb-4" aria-label={`${t.rating} out of 5`}>
            {[...Array(t.rating)].map((_, i) => <Star key={i} size={15} className="fill-gold-metallic text-gold-metallic" />)}
          </div>
          <blockquote className="text-sm text-cafe-noir/70 leading-relaxed flex-grow line-clamp-6">{t.quote}</blockquote>
          <figcaption className="mt-5 pt-4 border-t border-cafe-noir/10 text-sm text-cafe-noir/80">
            <span className="mr-1.5">{t.flag}</span>{t.name} · <span className="text-cafe-noir/55">{t.location}</span>
          </figcaption>
        </figure>
      ))}
    </Reveal>
  </section>
);

const CallToActionSection = () => (
<section 
  className="py-24 relative bg-cover bg-center"
  style={{
    backgroundImage: `url(${elephant})`,
    backgroundBlendMode: 'overlay',
    backgroundColor: 'rgba(0,0,0,0.6)'
  }}
>
  <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-transparent"></div>
  <div className="container mx-auto px-4 relative z-10">
    <div className="max-w-xl text-white">
      <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-6 leading-tight">Begin your African adventure today</h2>
      <p className="text-xl mb-8">Let us create a tailored safari experience that will exceed your expectations and create memories to last a lifetime.</p>
      <div className="flex flex-col sm:flex-row gap-4">
        <Link 
  to="/enquire"
  className="bg-hunyadi-yellow hover:bg-brown-sugar hover:text-white text-cafe-noir px-8 py-3.5 rounded-full text-lg font-medium transition-colors inline-block"
>
  Plan Your Safari
</Link>
<Link 
  to="/contact"
  className="bg-transparent border border-white hover:bg-white hover:text-cafe-noir px-8 py-3.5 rounded-full text-lg font-medium transition-colors inline-block"
>
  Contact Us
</Link>
      </div>
    </div>
  </div>
</section>
);




// This component is meant to be integrated into your existing Home page
const BlogSection = () => {
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRandomPosts = async () => {
      try {
        setIsLoading(true);
        
        // Get all blog posts from your local data
        const allPosts = getBlogPosts();
        
        // If you have fewer than 3 posts, use all of them
        if (allPosts.length <= 3) {
          setPosts(allPosts);
        } else {
          // Get 3 random posts
          const randomPosts = getRandomPosts(allPosts, 3);
          setPosts(randomPosts);
        }
        
        setIsLoading(false);
        
      } catch (err) {
        console.error("Error fetching posts:", err);
        setError(err.message);
        setIsLoading(false);
      }
    };

    fetchRandomPosts();
  }, []);

  // Function to get random posts
  const getRandomPosts = (posts, count) => {
    const shuffled = [...posts].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  };

  // Fallback data if no posts are available
  const fallbackPosts = [
    {
      id: 'fallback-1',
      title: "Top 10 Wildlife Photography Tips for Your Safari",
      excerpt: "Capture breathtaking moments with these expert tips from professional wildlife photographers.",
      formattedDate: "Mar 15, 2025",
      categories: ["Photography"],
      featuredImage: "https://images.unsplash.com/photo-1549366021-9f761d040a94?w=600&h=400&fit=crop",
      slug: "wildlife-photography-tips-safari"
    },
    {
      id: 'fallback-2',
      title: "What to Pack for Your First African Safari Adventure",
      excerpt: "Essential items and expert recommendations for a comfortable and memorable safari experience.",
      formattedDate: "Feb 28, 2025",
      categories: ["Travel Tips"],
      featuredImage: "https://images.unsplash.com/photo-1516426122078-c23e76319801?w=600&h=400&fit=crop",
      slug: "what-to-pack-african-safari"
    },
    {
      id: 'fallback-3',
      title: "Conservation Success: Rhino Population Growing in Kenya",
      excerpt: "Learn about the positive impact of conservation efforts and how travelers can contribute.",
      formattedDate: "Jan 20, 2025",
      categories: ["Conservation"],
      featuredImage: "https://images.unsplash.com/photo-1564349683136-77e08dba1ef7?w=600&h=400&fit=crop",
      slug: "rhino-conservation-success-kenya"
    }
  ];

  // Use fallback posts if loading failed or no posts were returned
  const displayPosts = posts.length > 0 ? posts : fallbackPosts;

  const navigate = useNavigate();

  // Handle navigation to blog page
  const handleBlogNavigation = () => {
    navigate('/blog');
  };

  return (
    <section className="py-24 lg:py-32 bg-eggshell">
      <div className="container mx-auto px-4">
        <div className="max-w-xl mx-auto text-center mb-16">
          <div className="flex items-center justify-center mb-4">
            <div className="h-px w-10 bg-clay mr-4"></div>
            <p className="text-asparagus font-medium uppercase tracking-wider">Safari Insights</p>
            <div className="h-px w-10 bg-clay ml-4"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-6 text-cafe-noir">From our blog</h2>
          <p className="text-brown-sugar text-lg">Expert advice, travel tips, and inspiring stories from the African wilderness.</p>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-moss-green"></div>
          </div>
        ) : error ? (
          <div className="text-center py-6">
            <p className="text-claret mb-4">Unable to load blog posts.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {fallbackPosts.map((post) => (
                <BlogPostCard key={post.id} post={post} />
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {displayPosts.map((post) => (
              <BlogPostCard key={post.id} post={post} />
            ))}
          </div>
        )}
        
        <div className="text-center mt-12">
          <button 
            onClick={handleBlogNavigation}
            className="bg-moss-green hover:bg-asparagus text-eggshell px-8 py-4 rounded-full font-medium transition hover:-translate-y-0.5 inline-flex items-center"
          >
            Visit Our Blog
            <ArrowRight className="ml-2" size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};

// Separated blog post card component for cleaner code
const BlogPostCard = ({ post }) => {
  const navigate = useNavigate();
  
  // Handle navigation to individual blog post
  const handlePostClick = () => {
    navigate(`/blog/${post.slug}`);
  };

  return (
    <div className="bg-cornsilk rounded-xl overflow-hidden border border-ink/10 transition-colors">
      <div className="h-60 overflow-hidden">
        <img
          src={post.featuredImage}
          alt={post.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=600&h=400&fit=crop';
          }}
        />
      </div>
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <span className="bg-hunyadi-yellow/20 text-cafe-noir px-3 py-1 rounded-full text-sm font-medium">
            {post.categories?.[0] || 'Safari'}
          </span>
          <span className="text-cafe-noir text-opacity-70 text-sm flex items-center">
            <Calendar size={14} className="mr-1" />
            {post.formattedDate}
          </span>
        </div>
        <h3 className="text-xl font-normal mb-2 text-cafe-noir">{post.title}</h3>
        <p className="text-brown-sugar mb-4">{post.excerpt}</p>
        <button 
          onClick={handlePostClick}
          className="text-hunyadi-yellow hover:text-gold-metallic font-medium inline-flex items-center group"
        >
          Read More
          <ArrowRight className="ml-2 transform group-hover:translate-x-1 transition-transform" size={16} />
        </button>
      </div>
    </div>
  );
};





const Home = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

return (
  <div>
    <PageMeta
      title="Kenya, Tanzania & Rwanda Safari Tours"
      description="Small-group African safaris across Kenya, Tanzania, Rwanda, South Africa and Zanzibar — Masai Mara migrations, gorilla trekking and more, planned by local guides."
      path="/"
    />
    <HeroBanner />
    <FeatureSection />
    <WaysToTravel />
    <DestinationsSection />
    <FeaturedExcursions />
    <AboutTeaser />
    <TestimonialsSection />
    <BlogSection />
    <CallToActionSection />
    
    {/* <Footer /> */}
  </div>
);
};

export default Home;